import { Component, Element, Host, h, Prop, State, Watch, forceUpdate, Listen } from '@stencil/core';
import { IntlShape } from '@formatjs/intl';
import parseJson from '../../utils/parse-json';
import { state } from '../../store';
import { getAccountUrl, getFeatureFlag, getGlobalSupportPortalUrl, getShouldUseAccountOwdFeatureFlagData } from '../../utils/header-footer';
import shadowRootParentOrDocument from '../../utils/generic-functions';
import { CamObject, UserObject } from '../../types/types';

@Component({
  tag: 'aui-footer',
  styleUrl: 'aui-footer.scss',
  shadow: false,
})
export class AuiFooter {
  @Listen('s-intlupdate', { target: 'window' })
  intlUpdateHandler() {
    forceUpdate(this);
  }

  @Element() element: HTMLAuiFooterElement;

  @Prop({ reflect: true }) cam: CamObject | string;

  @Prop() user: UserObject | string;

  @Prop({ attribute: 'termsurl' }) termsUrl = '';

  @Prop({ attribute: 'supportcasesurl' }) supportCasesUrl;

  @Prop({ attribute: 'i18n' }) i18n?: Record<string, string> | string = {};

  @Prop({ attribute: 'shoulduseaccountowd' }) shouldUseAccountOwd?: boolean = false;

  @Prop({ attribute: 'chaturl' }) chatUrl?: string = '';

  @Prop({ attribute: 'hidecontact' }) hideContact?: boolean = false;

  @State() camJson: CamObject = {};

  @State() shouldUseAccountOwdState = this.shouldUseAccountOwd;

  @State() userJson: UserObject = {};

  @State() i18nJson: Record<string, string> = {};

  @Watch('cam')
  watchCam(newValue) {
    const errorMessage = "Please provide valid JSON string on footer's cam attribute";
    const parsedCamJson = parseJson(newValue, errorMessage, {});
    this.camJson = { ...parsedCamJson };
  }

  @Watch('shouldUseAccountOwd')
  watchShouldUseAccountOwd(shouldUseAccountOwdOverride) {
    this.shouldUseAccountOwdState = !!shouldUseAccountOwdOverride;
  }

  @Watch('user')
  watchUser(newValue) {
    const errorMessage = "Please provide valid JSON string on footer's user attribute";
    const parsedUserJson = parseJson(newValue, errorMessage, {});
    this.userJson = { ...parsedUserJson };
  }

  @Watch('i18n')
  watchI18n() {
    this.i18nJson = parseJson(this.i18n, "Please provide valid JSON string on header's i18n attribute", {});
  }

  intl: IntlShape;

  camPhotoFailedToLoad = false;

  #openDialog() {
    const dialog = shadowRootParentOrDocument(this.element).getElementById('footer-contact-dialog');
    if (dialog.closest('s-popper') || dialog.closest('aui-sidenav')) {
      dialog.remove();
      if (shadowRootParentOrDocument(this.element).body) {
        shadowRootParentOrDocument(this.element).body.appendChild(dialog);
      } else {
        shadowRootParentOrDocument(this.element).appendChild(dialog);
      }
    }
    dialog.setAttribute('open', '');
  }

  static get accountHome() {
    switch (window.aui.env) {
      case 'local':
        return 'https://local.account.avalara.io';
      case 'ci':
        return 'https://ci.account.avalara.io';
      case 'qa':
      case 'uat':
        return 'https://qa.account.avalara.io';
      case 'sbx':
        return 'https://account.avalara.com';
      case 'prod':
      default:
        return 'https://account.avalara.com';
    }
  }

  get #auiFooterText() {
    const { intl } = this;
    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
    const copyIcon = <span>&copy;</span>;

    return {
      contactSupport:
        this.i18nJson['auiFooter.contactSupport'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.contactSupport',
          defaultMessage: 'Contact Support',
        }),
      ariaLabelContact:
        this.i18nJson['auiFooter.ariaLabel.contact'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.ariaLabel.contact',
          defaultMessage: 'Open Contact Us dialog',
        }),
      contactButton:
        this.i18nJson['auiFooter.contactButton'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.contactButton',
          defaultMessage: 'Contact',
        }),
      chatPrefix:
        this.i18nJson['auiFooter.chatPrefix'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.chatPrefix',
          defaultMessage: 'Need assistance?',
        }),
      chatLink:
        this.i18nJson['auiFooter.chatLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.chatLink',
          defaultMessage: 'Chat with an implementation agent',
        }),
      contactDialogHeader:
        this.i18nJson['auiFooter.contactDialogHeader'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.contactDialogHeader',
          defaultMessage: 'Contact',
        }),
      termsLink:
        this.i18nJson['auiFooter.termsLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.termsLink',
          defaultMessage: 'Terms',
        }),
      privacyLink:
        this.i18nJson['auiFooter.privacyLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.privacyLink',
          defaultMessage: 'Privacy',
        }),
      ariaLabelCam:
        this.i18nJson['auiFooter.ariaLabel.cam'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.ariaLabel.cam',
          defaultMessage: 'Customer Account Manager',
        }),
      camGreeting1:
        this.i18nJson['auiFooter.camGreeting1'] ||
        intl.formatMessage(
          {
            id: 'skylab.auiFooter.camGreeting1',
            defaultMessage: "Hi, I'm {CAM_NAME}.",
          },
          {
            CAM_NAME: this.camJson.name as string,
          },
        ),
      camGreeting2:
        this.i18nJson['auiFooter.camGreeting2'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.camGreeting2',
          defaultMessage: 'I can help you with your account and other Avalara resources.',
        }),
      scheduleConversationLink:
        this.i18nJson['auiFooter.scheduleConversationLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.scheduleConversationLink',
          defaultMessage: 'Schedule a conversation',
        }),
      helpCenterLink:
        this.i18nJson['auiFooter.helpCenterLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.helpCenterLink',
          defaultMessage: 'Knowledge Center',
        }),
      avalaraUniversityLink:
        this.i18nJson['auiFooter.avalaraUniversityLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.avalaraUniversityLink',
          defaultMessage: 'Avalara University',
        }),
      avalaraDotComLink:
        this.i18nJson['auiFooter.avalaraDotComLink'] ||
        intl.formatMessage({
          id: 'skylab.auiFooter.avalaraDotComLink',
          defaultMessage: 'Avalara.com',
        }),
      avalaraInc:
        this.i18nJson['auiFooter.avalaraInc'] ||
        intl.formatMessage(
          {
            id: 'skylab.auiFooter.avalaraInc',
            defaultMessage: '{icon} Avalara, Inc.',
          },
          {
            icon: copyIcon,
          },
        ),
    };
  }

  get #hrefs() {
    const cam = this.camJson;
    const camPhone = cam.phone as string;

    const accountUrl = getAccountUrl(this.shouldUseAccountOwdState);
    const loggedInSupportCasesUrl = getGlobalSupportPortalUrl();

    return {
      camEmail: this.#isCamValid && cam.email ? `mailto:${cam.email}` : '',
      camPhone: this.#isCamValid && cam.phone ? `tel:${camPhone.replace(/\s/g, '').replace('ext.', 'p')}` : '', // TODO: verify this is formatting correctly and works for international #s once this Jira ticket is resolved: https://jira.avalara.com/browse/IBST-22986
      camScheduling: this.#isCamValid && cam.schedulingUrl ? cam.schedulingUrl : '',
      supportCases: this.#isLoggedIn ? loggedInSupportCasesUrl : 'https://help.avalara.com/Contact_Avalara/Submit_a_Case',
      productsUsage: `${accountUrl}/goTo/products`,
      terms: this.termsUrl || 'https://www.avalara.com/terms',
    };
  }

  get #isCamValid() {
    return this.camJson.isValid;
  }

  get #isLoggedIn() {
    return this.userJson.isLoggedIn;
  }

  #onCamPhotoError() {
    this.camPhotoFailedToLoad = true;
  }

  #getSupportCasesEl() {
    const className = ` flex align-items-center ${this.#isCamValid ? 'margin-top-md' : 'margin-bottom-xs'}`;
    const dataRef = `aui-footer.${this.#isCamValid ? 'cam' : 'default'}-dialog.support-cases`;
    return (
      <a class={className} href={this.#hrefs.supportCases} data-ref={dataRef} target="_blank">
        <s-icon name="support" class="pad-end-sm" aria-hidden="true"></s-icon>
        {this.#auiFooterText.contactSupport}
      </a>
    );
  }

  async componentWillLoad() {
    // Skip fetching the feature flag if an explicit override attribute is passed
    if (this.shouldUseAccountOwdState) return Promise.resolve();
    return getFeatureFlag({
      feature: getShouldUseAccountOwdFeatureFlagData(),
      setFlagValue: flagValue => {
        this.shouldUseAccountOwdState = flagValue;
      },
    });
  }

  componentWillUpdate() {
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  connectedCallback() {
    this.watchCam(this.cam);
    this.watchUser(this.user);
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  render() {
    if (!this.intl) return null;
    const { camJson } = this;
    const { camEmail, camPhone, camScheduling } = this.#hrefs;
    const supportCasesEl = this.#getSupportCasesEl();

    return (
      <Host>
        <footer>
          <s-container>
            {this.chatUrl && (
              <s-row>
                <s-col data-ref="aui-footer.chat-link-col" class="pad-start-none pad-end-none">
                  <span class="text-sm-strong">{this.#auiFooterText.chatPrefix}</span>
                  <a href={this.chatUrl} target="_blank">
                    {this.#auiFooterText.chatLink}
                  </a>
                </s-col>
              </s-row>
            )}
            <span data-ref="aui-footer.main-span">
              <span data-ref="aui-footer.links-span">
                <a href="https://www.avalara.com" data-ref="aui-footer.avalara-dot-com" target="_blank">
                  {this.#auiFooterText.avalaraDotComLink}
                </a>
                {!this.chatUrl && !this.hideContact && (
                  <button type="button" class="link" onClick={() => this.#openDialog()} data-ref="aui-footer.open-contact-dialog" aria-label={this.#auiFooterText.ariaLabelContact}>
                    {this.#auiFooterText.contactButton}
                  </button>
                )}
                <a href={this.#hrefs.terms} data-ref="aui-footer.terms" target="_blank">
                  {this.#auiFooterText.termsLink}
                </a>
                <a href="https://www.avalara.com/us/en/legal/privacy-policy.html" data-ref="aui-footer.privacy-policy" target="_blank">
                  {this.#auiFooterText.privacyLink}
                </a>
              </span>
              <span data-ref="copyright">{this.#auiFooterText.avalaraInc}</span>
            </span>
          </s-container>
          {this.#isCamValid ? (
            <s-dialog data-ref="aui-footer.cam-dialog" id="footer-contact-dialog">
              <div slot="header">{this.#auiFooterText.contactDialogHeader}</div>
              <div slot="body">
                <div class="flex pad-bottom-sm">
                  <div data-ref="cam-photo-container">
                    {camJson.photoUrl && !this.camPhotoFailedToLoad ? (
                      <img alt={this.#auiFooterText.ariaLabelCam} src={camJson.photoUrl as string} class="pad-end-sm" onError={() => this.#onCamPhotoError()} />
                    ) : (
                      <s-icon aria-hidden="true" name="user"></s-icon>
                    )}
                  </div>
                  <div data-ref="cam-text" class="inline-flex flex-dir-col">
                    <div>{this.#auiFooterText.camGreeting1}</div>
                    <div>{this.#auiFooterText.camGreeting2}</div>
                  </div>
                </div>
                <div class="flex flex-dir-col">
                  {camJson.schedulingUrl ? (
                    <a class=" flex align-items-center" href={camScheduling as string} data-ref="aui-footer.cam-dialog.schedule" rel="noopener" target="_blank">
                      <s-icon name="calendar" class="pad-end-sm" aria-hidden="true"></s-icon>
                      {this.#auiFooterText.scheduleConversationLink}
                    </a>
                  ) : (
                    ''
                  )}
                  <a class="flex align-items-center margin-top-md" href={camEmail} data-ref="aui-footer.cam-dialog.email">
                    <s-icon name="email" class="pad-end-sm" aria-hidden="true"></s-icon>
                    {camJson.email}
                  </a>
                  {camJson.phone ? (
                    <a class=" flex align-items-center margin-top-md" href={camPhone} data-ref="aui-footer.cam-dialog.phone">
                      <s-icon name="phone" class="pad-end-sm" aria-hidden="true"></s-icon>
                      {camJson.phone}
                    </a>
                  ) : (
                    ''
                  )}
                </div>
                <hr />
                <div class="flex flex-dir-col">
                  <a class="flex align-items-center" href="https://knowledge.avalara.com/" data-ref="aui-footer.cam-dialog.help-center" target="_blank">
                    <s-icon name="help-circle" class="pad-end-sm" aria-hidden="true"></s-icon>
                    {this.#auiFooterText.helpCenterLink}
                  </a>
                  <a class=" flex align-items-center margin-top-md" href="https://training.avalara.com" data-ref="aui-footer.cam-dialog.avalara-u" target="_blank">
                    <s-icon name="grad-cap" class="pad-end-sm" aria-hidden="true"></s-icon>
                    {this.#auiFooterText.avalaraUniversityLink}
                  </a>
                  {supportCasesEl}
                </div>
              </div>
            </s-dialog>
          ) : (
            <s-dialog data-ref="aui-footer.default-dialog" id="footer-contact-dialog">
              <div slot="header">{this.#auiFooterText.contactDialogHeader}</div>
              <div class="flex flex-dir-col" slot="body">
                <a class="margin-bottom-md flex align-items-center" href="https://knowledge.avalara.com/" data-ref="aui-footer.default-dialog.help-center" target="_blank">
                  <s-icon name="help-circle" class="pad-end-sm" aria-hidden="true"></s-icon>
                  {this.#auiFooterText.helpCenterLink}
                </a>
                <a class="margin-bottom-md flex align-items-center" href="https://training.avalara.com" data-ref="aui-footer.default-dialog.avalara-u" target="_blank">
                  <s-icon name="grad-cap" class="pad-end-sm" aria-hidden="true"></s-icon>
                  {this.#auiFooterText.avalaraUniversityLink}
                </a>
                {supportCasesEl}
              </div>
            </s-dialog>
          )}
        </footer>
      </Host>
    );
  }
}
